import { DataExplorerViewCollection } from './DataExplorerViewCollection';
import { DataExplorerDisplayCard } from './DataExplorerDisplayCard';
import { DataExplorerDisplayListItem } from './DataExplorerDisplayListItem';
import { DataExplorerDisplayActionMenu } from './DataExplorerDisplayActionMenu';
import type {
  DataExplorerDisplayCardProps,
  DataExplorerDisplayCollectionViewProps,
  DataExplorerDisplayCollectionItemContext,
  DataExplorerDisplayCollectionItemPropsResolver,
  DataExplorerDisplayItem,
  DataExplorerDisplayListItemProps,
} from './types';
import { cn } from './utils';

function resolveDisplayItemProps<TProps, TSource>(
  resolver:
    | DataExplorerDisplayCollectionItemPropsResolver<TProps, TSource>
    | undefined,
  context: DataExplorerDisplayCollectionItemContext<TSource>,
) {
  if (!resolver) {
    return undefined;
  }

  return typeof resolver === 'function' ? resolver(context) : resolver;
}

function renderDefaultItem<TSource>(
  context: DataExplorerDisplayCollectionItemContext<TSource>,
  onOpenItem?: (item: DataExplorerDisplayItem<TSource>) => void,
  options?: {
    appearance?: DataExplorerDisplayCollectionViewProps<TSource>['appearance'];
    cardProps?: DataExplorerDisplayCollectionViewProps<TSource>['cardProps'];
    listItemProps?: DataExplorerDisplayCollectionViewProps<TSource>['listItemProps'];
    renderMedia?: DataExplorerDisplayCollectionViewProps<TSource>['renderMedia'];
    renderActions?: DataExplorerDisplayCollectionViewProps<TSource>['renderActions'];
  },
) {
  const { item, layout } = context;
  const interactive = Boolean(onOpenItem) && !item.disabled;
  const renderedActions = options?.renderActions
    ? options.renderActions(context)
    : item.actions?.length
      ? (
          <DataExplorerDisplayActionMenu
            item={item}
            actions={item.actions}
            appearance={options?.appearance}
          />
        )
      : undefined;
  const sharedProps: Partial<DataExplorerDisplayCardProps<TSource>> = {
    media: options?.renderMedia ? options.renderMedia(context) : undefined,
    actions: renderedActions,
    appearance: options?.appearance,
    interactive,
    onClick: interactive ? () => onOpenItem?.(item) : undefined,
  };

  if (layout === 'list') {
    const listItemProps = resolveDisplayItemProps<
      DataExplorerDisplayListItemProps<TSource>,
      TSource
    >(options?.listItemProps, context);

    return (
      <DataExplorerDisplayListItem
        {...sharedProps}
        {...listItemProps}
        item={item}
        className={cn(sharedProps.className, listItemProps?.className)}
      />
    );
  }

  const cardProps = resolveDisplayItemProps<
    DataExplorerDisplayCardProps<TSource>,
    TSource
  >(options?.cardProps, context);

  return (
    <DataExplorerDisplayCard
      {...sharedProps}
      {...cardProps}
      item={item}
      className={cn(sharedProps.className, cardProps?.className)}
    />
  );
}

export function DataExplorerDisplayCollectionView<TSource = unknown>({
  items,
  activeViewId = 'display',
  layout = 'grid',
  appearance,
  selectedIds,
  empty,
  loading,
  loadingNode,
  footer,
  overlay,
  className,
  classNames,
  scrollable,
  wrapItems,
  measuredGrid,
  itemsStyle,
  itemStyle,
  getItemClassName,
  cardProps,
  listItemProps,
  renderMedia,
  renderActions,
  renderItem,
  onOpenItem,
  onSelectionChange,
}: DataExplorerDisplayCollectionViewProps<TSource>) {
  return (
    <DataExplorerViewCollection<
      DataExplorerDisplayItem<TSource>,
      undefined,
      string
    >
      activeViewId={activeViewId}
      items={items}
      layout={layout}
      selectedIds={selectedIds}
      className={className}
      classNames={classNames}
      empty={empty}
      loading={loading}
      loadingNode={loadingNode}
      footer={footer}
      overlay={overlay}
      scrollable={scrollable}
      wrapItems={wrapItems}
      measuredGrid={measuredGrid}
      itemsStyle={itemsStyle}
      itemStyle={
        typeof itemStyle === 'function'
          ? ({ item, index = 0, itemId = '', selected = false }) =>
              itemStyle({
                item,
                index,
                itemId,
                selected,
                activeViewId,
                layout,
              })
          : itemStyle
      }
      getItemId={(item) => item.id}
      getItemClassName={
        getItemClassName
          ? ({ item, index = 0, itemId = '', selected = false }) =>
              getItemClassName({
                item,
                index,
                itemId,
                selected,
                activeViewId,
                layout,
              })
          : undefined
      }
      renderItem={({ item, index }) =>
        (() => {
          const context: DataExplorerDisplayCollectionItemContext<TSource> = {
            item,
            index: index ?? 0,
            itemId: item.id,
            selected: selectedIds?.includes(item.id) ?? false,
            activeViewId,
            layout,
          };

          return renderItem
            ? renderItem(item, context.index, context)
            : renderDefaultItem(context, onOpenItem, {
                cardProps,
                listItemProps,
                appearance,
                renderMedia,
                renderActions,
              });
        })()
      }
      onOpenItem={onOpenItem}
      onSelectionChange={onSelectionChange}
    />
  );
}
